import { useEffect, useMemo, useState } from "react";
import { postMerge, pollMerge, normalizeMergeArrival } from "./mergeApi";

// Poll cadence: hit the status endpoint every second for the first stretch, then
// ease off to every two seconds once a merge is clearly taking a while. No fake
// progress bar — the UI just shows elapsed seconds beside a spinner.
export const POLL_INTERVAL_MS = 1000;
export const POLL_BACKOFF_MS = 2000;
export const POLL_BACKOFF_AFTER_TICKS = 10;

function networkErrorOutcome(err) {
  return {
    phase: "error",
    ok: false,
    error: err?.message || "The merge request could not be completed.",
    errorType: "operation_failed",
    attention: null,
    stale: false,
    canRetry: true,
  };
}

// A small imperative state machine driving one merge at a time: POST → (maybe)
// poll-with-backoff → settle on a terminal normalized outcome. It owns every
// fetch and timer so no component reimplements the dispatch/poll dance. Callers
// wire it to React state via the three setters. A monotonically increasing
// generation invalidates any in-flight fetch/timer whenever run/retry/reset is
// invoked, so a stale poll can never settle a newer attempt or double-fire.
function makeMergeRunner({ setPhase, setOutcome, setElapsedSeconds }) {
  let mounted = true;
  let generation = 0;
  let pollTimer = null;
  let elapsedTimer = null;
  let params = null;
  let tick = 0;
  let elapsed = 0;

  const clearTimers = () => {
    if (pollTimer) {
      clearTimeout(pollTimer);
      pollTimer = null;
    }
    if (elapsedTimer) {
      clearInterval(elapsedTimer);
      elapsedTimer = null;
    }
  };

  const settle = (outcome) => {
    if (!mounted) return;
    clearTimers();
    setOutcome(outcome);
    setPhase("settled");
  };

  const startElapsed = () => {
    elapsed = 0;
    setElapsedSeconds(0);
    elapsedTimer = setInterval(() => {
      if (!mounted) return;
      elapsed += 1;
      setElapsedSeconds(elapsed);
    }, 1000);
  };

  // scheduleNextPoll and poll are mutually recursive; forward-declare the
  // scheduler so poll can reference it without a use-before-define.
  let scheduleNextPoll;

  async function poll(pollUrl, myGeneration) {
    let outcome;
    try {
      outcome = normalizeMergeArrival(await pollMerge(pollUrl));
    } catch (err) {
      outcome = networkErrorOutcome(err);
    }
    if (!mounted || myGeneration !== generation) return;
    tick += 1;
    if (outcome.phase === "pending") {
      scheduleNextPoll(pollUrl, myGeneration);
    } else {
      settle(outcome);
    }
  }

  scheduleNextPoll = (pollUrl, myGeneration) => {
    const interval = tick >= POLL_BACKOFF_AFTER_TICKS ? POLL_BACKOFF_MS : POLL_INTERVAL_MS;
    pollTimer = setTimeout(() => poll(pollUrl, myGeneration), interval);
  };

  async function begin() {
    generation += 1;
    const myGeneration = generation;
    clearTimers();
    tick = 0;
    setOutcome(null);
    setPhase("running");
    startElapsed();

    let outcome;
    try {
      outcome = normalizeMergeArrival(await postMerge(params));
    } catch (err) {
      outcome = networkErrorOutcome(err);
    }
    if (!mounted || myGeneration !== generation) return;

    if (outcome.phase === "pending" && outcome.pollUrl) {
      scheduleNextPoll(outcome.pollUrl, myGeneration);
    } else {
      settle(outcome);
    }
  }

  return {
    run: (nextParams) => {
      params = nextParams;
      begin();
    },
    retry: () => {
      if (params) begin();
    },
    reset: () => {
      generation += 1;
      clearTimers();
      setOutcome(null);
      setPhase("idle");
      setElapsedSeconds(0);
    },
    destroy: () => {
      mounted = false;
      clearTimers();
    },
  };
}

/**
 * useMergeRunner — the shared client-side merge driver. Every merge UI (the
 * console and the media-outlet show page) runs through this hook so there is one
 * dispatch/normalize/poll/retry implementation.
 *
 * Returns:
 *   phase          — "idle" | "running" | "settled"
 *   outcome        — the normalized terminal outcome once settled (else null)
 *   elapsedSeconds — seconds since the current attempt began (for the spinner)
 *   run(params)    — start a merge; params = { type, sourceId, destinationId }
 *   retry()        — re-POST the same params (server re-dispatches or replays hooks)
 *   reset()        — cancel/clear back to idle
 */
export function useMergeRunner() {
  const [phase, setPhase] = useState("idle");
  const [outcome, setOutcome] = useState(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  const runner = useMemo(
    () => makeMergeRunner({ setPhase, setOutcome, setElapsedSeconds }),
    [setPhase, setOutcome, setElapsedSeconds],
  );

  useEffect(() => () => runner.destroy(), [runner]);

  return {
    phase,
    outcome,
    elapsedSeconds,
    run: runner.run,
    retry: runner.retry,
    reset: runner.reset,
  };
}
